import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  FormControlLabel,
  Switch,
  Typography,
  Box,
  Divider,
  Alert,
  CircularProgress,
} from "@mui/material";
import { CloudUpload as UploadIcon } from "@mui/icons-material";
import drinksApi from "../../api/drinks";
import { uploadApi } from "../../api/upload";

interface DrinkDetailModalProps {
  open: boolean;
  onClose: () => void;
  drinkId: string | null;
  onSuccess: () => void;
  readOnly?: boolean;
}

const DrinkDetailModal: React.FC<DrinkDetailModalProps> = ({
  open,
  onClose,
  drinkId,
  onSuccess,
  readOnly = false,
}) => {
  const [formData, setFormData] = useState<any>({
    name: "",
    type: "coffee",
    baseTier: "coffee",
    description: "",
    imageUrl: "",
    volumeMl: 300,
    quantityValue: "",
    quantityUnit: "g",
    temperature: "cold",
    isActive: true,
    isVegan: false,
    containsDairy: false,
    containsNuts: false,
    allergens: "",
    ingredients: "",
    calories: "",
    protein: "",
    carbs: "",
    fats: "",
    caffeineMg: "",
    sugar: "",
  });
  const [loading, setLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (readOnly) return;
    if (!e.target.files || e.target.files.length === 0) return;
    const file = e.target.files[0];
    setUploadingImage(true);
    setError(null);
    try {
      const response = await uploadApi.uploadImage(file, "drinks");
      setFormData((prev: any) => ({ ...prev, imageUrl: response.imageUrl }));
    } catch (err: any) {
      console.error(err);
      setError("Failed to upload image. Please try again or paste image URL.");
    } finally {
      setUploadingImage(false);
    }
  };

  useEffect(() => {
    if (open) {
      if (drinkId) {
        fetchDrink(drinkId);
      } else {
        setFormData({
          name: "",
          type: "coffee",
          baseTier: "coffee",
          description: "",
          imageUrl: "",
          volumeMl: 300,
          quantityValue: "",
          quantityUnit: "g",
          temperature: "cold",
          isActive: true,
          isVegan: false,
          containsDairy: false,
          containsNuts: false,
          allergens: "",
          ingredients: "",
          calories: "",
          protein: "",
          carbs: "",
          fats: "",
          caffeineMg: "",
          sugar: "",
        });
      }
      setError(null);
    }
  }, [open, drinkId]);

  const fetchDrink = async (id: string) => {
    setLoading(true);
    try {
      const res = await drinksApi.getDrinkById(id);
      if (res.drink) {
        const d = res.drink;
        setFormData({
          name: d.name || "",
          type: d.type || "coffee",
          baseTier: d.baseTier || d.type || "coffee",
          description: d.description || "",
          imageUrl: d.imageUrl || "",
          volumeMl: d.volumeMl ?? 300,
          quantityValue: d.quantity?.value ?? "",
          quantityUnit: d.quantity?.unit || "g",
          temperature: d.temperature || "cold",
          isActive: d.isActive !== undefined ? d.isActive : true,
          isVegan: d.isVegan || false,
          containsDairy: d.containsDairy || false,
          containsNuts: d.containsNuts || false,
          allergens: Array.isArray(d.allergens) ? d.allergens.join(", ") : "",
          ingredients: Array.isArray(d.ingredients) ? d.ingredients.join(", ") : "",
          calories: d.nutritionalInfo?.calories ?? "",
          protein: d.nutritionalInfo?.protein ?? "",
          carbs: d.nutritionalInfo?.carbs ?? "",
          fats: d.nutritionalInfo?.fats ?? "",
          caffeineMg: d.nutritionalInfo?.caffeineMg ?? "",
          sugar: d.nutritionalInfo?.sugar ?? "",
        });
      }
    } catch (err: any) {
      setError(err.message || "Failed to load drink details");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (readOnly) return;
    const { name, value, checked, type } = e.target;
    if (name === "type") {
      setFormData((prev: any) => ({
        ...prev,
        type: value,
        baseTier: value,
      }));
    } else {
      setFormData((prev: any) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      }));
    }
  };

  const handleSubmit = async () => {
    if (readOnly) return;
    if (!formData.name.trim()) {
      setError("Drink name is required");
      return;
    }

    const quantityValue = String(formData.quantityValue).trim();
    if (quantityValue && (!Number.isFinite(Number(quantityValue)) || Number(quantityValue) <= 0)) {
      setError("Quantity must be a positive number");
      return;
    }
    setLoading(true);
    setError(null);

    const payload = {
      name: formData.name.trim(),
      type: formData.type,
      baseTier: formData.baseTier,
      description: formData.description.trim(),
      imageUrl: formData.imageUrl.trim(),
      volumeMl: Number(formData.volumeMl),
      quantity: quantityValue ? { value: Number(quantityValue), unit: formData.quantityUnit } : null,
      temperature: formData.temperature,
      isActive: formData.isActive,
      isVegan: formData.isVegan,
      containsDairy: formData.containsDairy,
      containsNuts: formData.containsNuts,
      allergens: formData.allergens
        ? formData.allergens.split(",").map((s: string) => s.trim()).filter(Boolean)
        : [],
      ingredients: formData.ingredients
        ? formData.ingredients.split(",").map((s: string) => s.trim()).filter(Boolean)
        : [],
      nutritionalInfo: {
        calories: formData.calories !== "" ? Number(formData.calories) : undefined,
        protein: formData.protein !== "" ? Number(formData.protein) : undefined,
        carbs: formData.carbs !== "" ? Number(formData.carbs) : undefined,
        fats: formData.fats !== "" ? Number(formData.fats) : undefined,
        caffeineMg: formData.caffeineMg !== "" ? Number(formData.caffeineMg) : undefined,
        sugar: formData.sugar !== "" ? Number(formData.sugar) : undefined,
      },
    };

    try {
      if (drinkId) {
        await drinksApi.updateDrink(drinkId, payload);
      } else {
        await drinksApi.createDrink(payload);
      }
      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || "Failed to save drink");
    } finally {
      setLoading(false);
    }
  };

  if (readOnly) {
    const details = [
      ["Type", formData.type], ["Volume", `${formData.volumeMl} ml`],
      ["Quantity", formData.quantityValue !== "" ? `${formData.quantityValue} ${formData.quantityUnit}` : "Not set"],
      ["Temperature", formData.temperature], ["Availability", formData.isActive ? "Active" : "Inactive"],
      ["Ingredients", formData.ingredients || "Not provided"], ["Allergens", formData.allergens || "None listed"],
      ["Vegan", formData.isVegan ? "Yes" : "No"], ["Contains dairy", formData.containsDairy ? "Yes" : "No"],
      ["Contains nuts", formData.containsNuts ? "Yes" : "No"],
      ...[["Calories", "calories", "kcal"], ["Protein", "protein", "g"], ["Carbs", "carbs", "g"], ["Fats", "fats", "g"], ["Sugar", "sugar", "g"], ["Caffeine", "caffeineMg", "mg"]]
        .map(([label, key, unit]) => [label, formData[key] !== "" && formData[key] != null ? `${formData[key]} ${unit}` : "Not provided"]),
    ];
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>{loading ? "Drink details" : formData.name || "Drink details"}</DialogTitle>
        <DialogContent dividers>
          {error ? <Alert severity="error">{error}</Alert> : loading ? <CircularProgress size={24} /> : <>
            {formData.imageUrl && <Box component="img" src={formData.imageUrl} alt={formData.name} sx={{ width: "100%", height: 180, objectFit: "contain", mb: 2 }} />}
            {formData.description && <Typography variant="body2" sx={{ mb: 2 }}>{formData.description}</Typography>}
            <Box component="dl" sx={{ display: "grid", gridTemplateColumns: "minmax(100px, 0.6fr) minmax(0, 1fr)", gap: 1.5, m: 0 }}>
              {details.map(([label, value]) => <React.Fragment key={label}>
                <Typography component="dt" variant="body2" color="text.secondary">{label}</Typography>
                <Typography component="dd" variant="body2" sx={{ m: 0, overflowWrap: "anywhere" }}>{value}</Typography>
              </React.Fragment>)}
            </Box>
          </>}
        </DialogContent>
        <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>
        {drinkId ? "Edit Drink" : "Add New Drink"}
      </DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {/* Row 1: Name and Type */}
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "2fr 1fr" }, gap: 2 }}>
            <TextField
              label="Drink Name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              fullWidth
              required
            />
            <TextField
              select
              label="Type"
              name="type"
              value={formData.type}
              onChange={handleChange}
              fullWidth
            >
              <MenuItem value="coffee">Coffee</MenuItem>
              <MenuItem value="blend">Blend (Shake)</MenuItem>
            </TextField>
          </Box>

          {/* Description */}
          <TextField
            label="Description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            multiline
            rows={2}
            fullWidth
          />

          {/* Image Upload & Preview */}
          <Box sx={{ p: 2, bgcolor: "#f8fafc", borderRadius: 2, border: "1px solid #e2e8f0" }}>
            <Typography variant="caption" fontWeight={700} color="textSecondary" sx={{ mb: 1, display: "block" }}>
              DRINK IMAGE
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
              {formData.imageUrl && (
                <Box
                  component="img"
                  src={formData.imageUrl}
                  alt="Drink Preview"
                  sx={{
                    width: 72,
                    height: 72,
                    borderRadius: 1.5,
                    objectFit: "cover",
                    border: "1px solid #cbd5e1",
                  }}
                />
              )}
              <Button
                variant="outlined"
                component="label"
                startIcon={uploadingImage ? <CircularProgress size={16} /> : <UploadIcon />}
                disabled={uploadingImage}
                sx={{ textTransform: "none" }}
              >
                {uploadingImage ? "Uploading to S3..." : formData.imageUrl ? "Replace Image" : "Upload Image"}
                <input type="file" hidden accept="image/*" onChange={handleImageUpload} />
              </Button>
              <TextField
                label="Or Paste Image URL"
                name="imageUrl"
                size="small"
                value={formData.imageUrl}
                onChange={handleChange}
                sx={{ flexGrow: 1, minWidth: 220 }}
              />
            </Box>
          </Box>

          {/* Volume & Serving Temp */}
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
            <TextField label="Quantity" name="quantityValue" type="number" value={formData.quantityValue}
              onChange={handleChange} inputProps={{ min: 0, step: "any" }} fullWidth
              helperText="Serving amount, separate from volume. Leave blank if unconfirmed." />
            <TextField label="Quantity unit" name="quantityUnit" select value={formData.quantityUnit} onChange={handleChange} fullWidth>
              <MenuItem value="g">Grams</MenuItem><MenuItem value="ml">Millilitres</MenuItem>
            </TextField>
            <TextField
              label="Volume (ml)"
              name="volumeMl"
              type="number"
              value={formData.volumeMl}
              onChange={handleChange}
              fullWidth
            />
            <TextField
              select
              label="Serving Temp"
              name="temperature"
              value={formData.temperature}
              onChange={handleChange}
              fullWidth
            >
              <MenuItem value="cold">Chilled / Cold</MenuItem>
              <MenuItem value="hot">Hot</MenuItem>
              <MenuItem value="ambient">Ambient</MenuItem>
            </TextField>
          </Box>

          <Divider sx={{ my: 1 }} />
          <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 600 }}>
            Nutritional Information
          </Typography>

          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(6, 1fr)" }, gap: 2 }}>
            <TextField
              label="Calories (kcal)"
              name="calories"
              type="number"
              value={formData.calories}
              onChange={handleChange}
              fullWidth
            />
            <TextField
              label="Protein (g)"
              name="protein"
              type="number"
              value={formData.protein}
              onChange={handleChange}
              fullWidth
            />
            <TextField
              label="Carbs (g)"
              name="carbs"
              type="number"
              value={formData.carbs}
              onChange={handleChange}
              fullWidth
            />
            <TextField
              label="Fats (g)"
              name="fats"
              type="number"
              value={formData.fats}
              onChange={handleChange}
              fullWidth
            />
            <TextField
              label="Caffeine (mg)"
              name="caffeineMg"
              type="number"
              value={formData.caffeineMg}
              onChange={handleChange}
              fullWidth
            />
            <TextField
              label="Sugar (g)"
              name="sugar"
              type="number"
              value={formData.sugar}
              onChange={handleChange}
              fullWidth
            />
          </Box>

          <Divider sx={{ my: 1 }} />
          <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 600 }}>
            Dietary Flags & Allergens
          </Typography>

          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
            <FormControlLabel
              control={<Switch checked={formData.isVegan} onChange={handleChange} name="isVegan" />}
              label="Vegan"
            />
            <FormControlLabel
              control={<Switch checked={formData.containsDairy} onChange={handleChange} name="containsDairy" />}
              label="Contains Dairy"
            />
            <FormControlLabel
              control={<Switch checked={formData.containsNuts} onChange={handleChange} name="containsNuts" />}
              label="Contains Nuts"
            />
          </Box>

          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
            <TextField
              label="Allergens (comma separated)"
              name="allergens"
              value={formData.allergens}
              onChange={handleChange}
              placeholder="e.g. Dairy, Peanuts, Soy"
              fullWidth
            />
            <TextField
              label="Key Ingredients (comma separated)"
              name="ingredients"
              value={formData.ingredients}
              onChange={handleChange}
              placeholder="e.g. Cold Brew Coffee, Oat Milk, Vanilla"
              fullWidth
            />
          </Box>

          <Box sx={{ mt: 1 }}>
            <FormControlLabel
              control={<Switch checked={formData.isActive} onChange={handleChange} name="isActive" color="success" />}
              label="Active & Available in Catalog"
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} disabled={loading} color="inherit">
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading} sx={{ bgcolor: "#000", color: "#fff", "&:hover": { bgcolor: "#222" } }}>
          {loading ? "Saving..." : drinkId ? "Update Drink" : "Create Drink"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DrinkDetailModal;
